import React, { useState, useEffect, useCallback } from 'react';
import './ManualPlay.css';
import './EsporResults.css';

const API_BASE = '/api';

function ManualPlay({ onNavigate }) {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedBet, setSelectedBet] = useState(null);
  const [betLoading, setBetLoading] = useState(false);
  const [betResult, setBetResult] = useState(null);
  const [lastRefresh, setLastRefresh] = useState(null);
  const [loggedInAccounts, setLoggedInAccounts] = useState([]);
  const [selectedAccounts, setSelectedAccounts] = useState([]);
  const [globalMode, setGlobalMode] = useState('gizle');
  const [globalOddsMode, setGlobalOddsMode] = useState('gizli'); // 'gizli' | 'hizli' | 'tumu'
  const [rowModes, setRowModes] = useState({});
  const [rowOddsMode, setRowOddsMode] = useState({});
  const [only2X5, setOnly2X5] = useState(true);

  const token = localStorage.getItem('token');

  const fetchEvents = useCallback(async (triggerRefresh) => {
    setLoading(true);
    try {
      if (triggerRefresh) {
        await fetch(`${API_BASE}/goals/refresh-bulten`, {
          method: 'POST',
          headers: { Authorization: `Bearer ${token}` },
        });
      }
      const res = await fetch(`${API_BASE}/goals/events`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      if (data.success) {
        setEvents(data.data);
        setLastRefresh(new Date());
      }
    } catch (err) {
      console.error('Events fetch error:', err);
    }
    setLoading(false);
  }, [token]);

  // Apply 2x5 preset on first load, track known codes to avoid resetting user choices
  const firstLoadRef = React.useRef(true);
  const knownCodesRef = React.useRef(new Set());
  useEffect(() => {
    if (events.length === 0) return;
    const active = events.filter(e => e.status !== 5);
    if (firstLoadRef.current) {
      firstLoadRef.current = false;
      apply2x5(active);
      active.forEach(e => knownCodesRef.current.add(e.code));
      return;
    }
    // Sadece gerçekten yeni eklenen maçlara 2x5 kuralını uygula
    const newEvents = active.filter(e => !knownCodesRef.current.has(e.code));
    if (newEvents.length === 0) return;
    newEvents.forEach(e => knownCodesRef.current.add(e.code));
    setRowModes(prev => {
      const updated = { ...prev };
      for (const ev of newEvents) {
        const is2x5 = /2\s*[xX×]\s*5/i.test(ev.league || '');
        updated[ev.code] = is2x5 ? 'cizgi' : 'gizle';
      }
      return updated;
    });
    setRowOddsMode(prev => {
      const updated = { ...prev };
      for (const ev of newEvents) {
        const is2x5 = /2\s*[xX×]\s*5/i.test(ev.league || '');
        updated[ev.code] = is2x5 ? 'hizli' : 'gizli';
      }
      return updated;
    });
    // eslint-disable-next-line
  }, [events]);

  useEffect(() => {
    fetchEvents();
    const interval = setInterval(fetchEvents, 5000);
    return () => clearInterval(interval);
  }, [fetchEvents]);

  useEffect(() => {
    if (!token) return;
    const fetchAccounts = () => {
      fetch(`${API_BASE}/nesine/status`, { headers: { Authorization: `Bearer ${token}` } })
        .then(r => r.json())
        .then(d => {
          if (d.success) {
            const loggedIn = d.data.filter(a => a.isLoggedIn);
            setLoggedInAccounts(loggedIn);
            setSelectedAccounts(prev => {
              if (prev.length === 0) return loggedIn.map(a => a.username);
              return prev.filter(u => loggedIn.some(a => a.username === u));
            });
          }
        })
        .catch(() => {});
    };
    fetchAccounts();
    const interval = setInterval(fetchAccounts, 10000);
    return () => clearInterval(interval);
  }, [token]);

  /* ── Helpers ── */
  const clubOf = (full) => { const m = full?.match(/^(.+?)\s*\(([^)]+)\)$/); return m ? m[1].trim() : full || '?'; };
  const playerOf = (full) => { const m = full?.match(/^(.+?)\s*\(([^)]+)\)$/); return m ? m[2].trim() : ''; };

  const DAYS_TR = ['Pazar', 'Pazartesi', 'Salı', 'Çarşamba', 'Perşembe', 'Cuma', 'Cumartesi'];
  const fmtTime = (d) => {
    if (!d) return { line1: '-', line2: '' };
    const dt = new Date(typeof d === 'number' && d < 1e12 ? d * 1000 : d);
    const now = new Date();
    const trOpts = { timeZone: 'Europe/Istanbul' };
    const dtDay = dt.toLocaleDateString('tr-TR', { ...trOpts, day: '2-digit', month: '2-digit', year: '2-digit' });
    const nowDay = now.toLocaleDateString('tr-TR', { ...trOpts, day: '2-digit', month: '2-digit', year: '2-digit' });
    const isToday = dtDay === nowDay;
    const time = dt.toLocaleTimeString('tr-TR', { ...trOpts, hour: '2-digit', minute: '2-digit' });
    const datePart = dtDay;
    const dayName = dt.toLocaleDateString('tr-TR', { ...trOpts, weekday: 'long' });
    const dayNameShort = DAYS_TR.find(d => dayName.toLowerCase().startsWith(d.toLowerCase())) || dayName;
    if (isToday) return { line1: time, line2: datePart };
    return { line1: `${dayNameShort} ${time}`, line2: datePart };
  };

  const modeOf = (id) => rowModes[id] || globalMode;
  const setMode = (id, mode) => setRowModes(prev => ({ ...prev, [id]: mode }));
  const oddsModeFor = (id) => rowOddsMode[id] ?? globalOddsMode;
  const setRowOdds = (id, mode) => setRowOddsMode(prev => ({ ...prev, [id]: mode }));
  const setGlobalModeAll = (mode) => { setGlobalMode(mode); setRowModes({}); };
  const setGlobalOdds = (mode) => { setGlobalOddsMode(mode); setRowOddsMode({}); };

  /* ── Apply 2x5 preset: cizgi+hızlı for 2x5, gizle+gizli for others ── */
  const apply2x5 = (evts) => {
    const rm = {}; const ro = {};
    for (const ev of evts) {
      const is2x5 = /2\s*[xX×]\s*5/i.test(ev.league || '');
      rm[ev.code] = is2x5 ? 'cizgi' : 'gizle';
      ro[ev.code] = is2x5 ? 'hizli' : 'gizli';
      knownCodesRef.current.add(ev.code);
    }
    setRowModes(rm); setRowOddsMode(ro);
    setGlobalMode('gizle'); setGlobalOddsMode('gizli');
  };

  const apply2x5Full = (evts) => {
    const rm = {}; const ro = {};
    for (const ev of evts) {
      const is2x5 = /2\s*[xX×]\s*5/i.test(ev.league || '');
      rm[ev.code] = is2x5 ? 'cizgi' : 'gizle';
      ro[ev.code] = is2x5 ? 'tumu' : 'gizli';
      knownCodesRef.current.add(ev.code);
    }
    setRowModes(rm); setRowOddsMode(ro);
    setGlobalMode('gizle'); setGlobalOddsMode('gizli');
  };

  /* ── Match minute ── */
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    const timer = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(timer);
  }, []);

  const getMatchMinute = (league, startTime, status) => {
    if (status === 5) return { minute: 90, half: 0, label: 'MS' };
    if (!startTime) return null;
    const halfMatch = league?.match(/2\s*[xX×]\s*(\d+)/);
    const halfDurationMin = halfMatch ? parseInt(halfMatch[1], 10) : 5;
    const halfDurationMs = halfDurationMin * 60 * 1000;
    const halfBreakMs = 60 * 1000;
    const speedFactor = 45 / halfDurationMin;
    const startMs = typeof startTime === 'number' && startTime < 1e12 ? startTime * 1000 : new Date(startTime).getTime();
    const elapsedMs = now - startMs;
    if (elapsedMs < 0) return { minute: 0, half: 0, label: 'Başlamadı' };
    if (elapsedMs <= halfDurationMs) {
      const gameMinute = Math.floor((elapsedMs / 60000) * speedFactor);
      return { minute: Math.min(gameMinute, 45), half: 1, label: `${Math.min(gameMinute, 45)}'` };
    }
    if (elapsedMs <= halfDurationMs + halfBreakMs || status === 3) {
      return { minute: 45, half: 1, label: `45'` };
    }
    const secondHalfElapsed = elapsedMs - halfDurationMs - halfBreakMs;
    const gameMinute = 45 + Math.floor((secondHalfElapsed / 60000) * speedFactor);
    return { minute: gameMinute, half: 2, label: `${gameMinute}'` };
  };

  /* ── Goal Timeline (Çizgi) ── */
  const renderGoalTimeline = (event) => {
    const hg = (event.goalEvents || []).filter(g => g.team === 'home').map(g => g.minute);
    const ag = (event.goalEvents || []).filter(g => g.team === 'away').map(g => g.minute);
    const matchMin = getMatchMinute(event.league, event.startTime, event.status);
    if (!hg.length && !ag.length && (!matchMin || matchMin.minute <= 0)) return <div className="er-no-goals">Gol yok</div>;
    return (
      <div className="er-timeline">
        <div className="er-tl-goals er-tl-home">
          {hg.map((min, i) => <span key={i} className="er-tl-pill er-tl-h" style={{ left: `${(min / 120) * 100}%` }}>{min}'</span>)}
        </div>
        <div className="er-tl-bar">
          <div className="er-tl-half" />
          {matchMin && matchMin.minute > 0 && matchMin.label !== 'MS' && (
            <div className="mp-tl-dot" style={{ left: `${Math.min((matchMin.minute / 120) * 100, 100)}%` }} />
          )}
          {[15, 30, 45, 60, 75, 90, 105, 120].map(t => (
            <div key={t} className="er-tl-tick" style={{ left: `${(t / 120) * 100}%` }}><span className="er-tl-tick-l">{t}</span></div>
          ))}
        </div>
        <div className="er-tl-goals er-tl-away">
          {ag.map((min, i) => <span key={i} className="er-tl-pill er-tl-a" style={{ left: `${(min / 120) * 100}%` }}>{min}'</span>)}
        </div>
      </div>
    );
  };

  /* ── Goal List (Liste) ── */
  const renderGoalList = (event) => {
    const goals = (event.goalEvents || []).slice().sort((a, b) => a.minute - b.minute);
    if (!goals.length) return <div className="er-no-goals">Gol yok</div>;
    return (
      <div className="er-gl">
        {goals.map((g, i) => (
          <div key={i} className="er-gl-row">
            <span className="er-gl-left">{g.team === 'home' ? `${g.minute}'` : ''}</span>
            <span className="er-gl-ball">⚽</span>
            <span className="er-gl-right">{g.team === 'away' ? `${g.minute}'` : ''}</span>
          </div>
        ))}
      </div>
    );
  };

  /* ── Mode Buttons ── */
  const renderModeButtons = (id) => {
    const current = modeOf(id);
    const om = oddsModeFor(id);
    return (
      <div className="er-mode-btns">
        {[['cizgi', '📊'], ['liste', '📋'], ['gizle', '✕']].map(([m, icon]) => (
          <button key={m} className={`er-mbtn ${current === m ? 'er-mbtn-on' : ''}`}
            onClick={() => setMode(id, m)} title={m}>{icon}</button>
        ))}
        <button className={`er-mbtn ${om === 'hizli' ? 'er-mbtn-on' : ''}`}
          onClick={() => setRowOdds(id, om === 'hizli' ? 'gizli' : 'hizli')} title="Hızlı Oranlar">⚡</button>
        <button className={`er-mbtn ${om === 'tumu' ? 'er-mbtn-on' : ''}`}
          onClick={() => setRowOdds(id, om === 'tumu' ? 'gizli' : 'tumu')} title="Tüm Oranlar">📈</button>
      </div>
    );
  };

  /* ── Market Type fallback labels (only for disabled placeholders) ── */
  const MARKET_NAMES = {
    629: 'Maç Sonucu', 630: 'Maç Kalanı', 631: 'Çifte Şans',
    632: 'Hand. MS', 633: 'Toplam Gol A/Ü', 634: 'Sıradaki Gol',
    635: 'MS Alt/Üst', 636: 'Ev A/Ü', 637: 'Ev Kaç Gol',
    638: 'Dep Kaç Gol', 640: '1Y Sonucu', 641: '1Y Çifte Şans',
    642: '1Y Handikap', 643: '1Y Alt/Üst', 644: 'Dep A/Ü',
    645: 'Gol Aralığı', 792: 'Hand. 1Y', 793: 'İY Kalanı',
    794: '1Y Hand. MS',
  };

  // Fixed market slot definitions per group — always shown in this order
  const MARKET_GROUPS = [
    { label: 'HIZLI ERİŞİM', slots: [
      { mtid: 793, sov: null, cols: 3 },
      { mtid: 634, sov: null, cols: 3 },
      { mtid: 630, sov: null, cols: 3 },
    ]},
    { label: 'MAÇ SONUCU', slots: [
      { mtid: 629, sov: null, cols: 3 },
      { mtid: 630, sov: null, cols: 3 },
      { mtid: 631, sov: null, cols: 3 },
      { mtid: 632, sov: null, cols: 3 },
    ]},
    { label: 'ALT/ÜST', slots: [
      { mtid: 633, sov: null, cols: 2, multi: true },
      { mtid: 635, sov: null, cols: 2, multi: true },
    ]},
    { label: 'GOL', slots: [
      { mtid: 634, sov: null, cols: 3 },
      { mtid: 637, sov: null, cols: 4 },
      { mtid: 638, sov: null, cols: 4 },
    ]},
    { label: 'TARAF ALT/ÜST', slots: [
      { mtid: 636, sov: null, cols: 2, multi: true },
      { mtid: 644, sov: null, cols: 2, multi: true },
      { mtid: 645, sov: null, cols: 2, multi: true },
    ]},
    { label: '1. YARI', slots: [
      { mtid: 640, sov: null, cols: 3 },
      { mtid: 793, sov: null, cols: 3 },
      { mtid: 641, sov: null, cols: 3 },
      { mtid: 643, sov: null, cols: 2, multi: true },
      { mtid: 642, sov: null, cols: 2 },
      { mtid: 792, sov: null, cols: 2 },
      { mtid: 794, sov: null, cols: 3 },
    ]},
  ];

  /* ── SOV label for handicap & sıradaki gol markets ── */
  const HANDICAP_MTIDS = new Set([632, 642, 792, 794]);
  const sovLabel = (market) => {
    const sov = market.sov;
    if (sov == null) return '';
    if (market.mtid === 634) return ` (${sov}. Gol)`;
    if (HANDICAP_MTIDS.has(market.mtid)) {
      return sov >= 0 ? ` (${sov}:0)` : ` (0:${Math.abs(sov)})`;
    }
    return '';
  };

  /* ── Odds Panel (grouped cards, fixed slots) ── */
  const renderOddsPanel = (event, oddsMode) => {
    const mkts = event.markets || [];
    // Index markets by mtid (some mtids have multiple SOV entries)
    const mktMap = {};
    for (const m of mkts) {
      const key = m.mtid;
      if (!mktMap[key]) mktMap[key] = [];
      mktMap[key].push(m);
    }

    const groups = oddsMode === 'hizli'
      ? MARKET_GROUPS.filter(g => g.label === 'HIZLI ERİŞİM')
      : MARKET_GROUPS;

    return (
      <div className="mp-odds-groups">
        {groups.map((group) => (
          <div key={group.label} className="mp-odds-group">
            <div className="mp-odds-group-hdr">{group.label}</div>
            <div className="mp-odds-group-body">
              {group.slots.map((slot) => {
                const arr = mktMap[slot.mtid] || [];
                // multi: show all SOV variants; single: show first
                const marketsToShow = slot.multi ? arr.sort((a, b) => (a.sov || 0) - (b.sov || 0)) : arr.length > 0 ? [arr[0]] : [];

                if (marketsToShow.length === 0) {
                  // Disabled placeholder
                  return (
                    <div key={slot.mtid} className="mp-odds-card mp-odds-card-off">
                      <div className="mp-odds-card-header">{MARKET_NAMES[slot.mtid] || `Market ${slot.mtid}`}</div>
                      <div className="mp-odds-card-body">
                        {Array.from({ length: slot.cols }).map((_, i) => (
                          <div key={i} className="mp-odds-cell mp-odds-cell-empty">
                            <span className="mp-odds-cell-lbl">-</span>
                            <span className="mp-odds-cell-val">-</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  );
                }

                return marketsToShow.map((market) => (
                  <div key={`${market.mtid}-${market.sov || 0}-${market.id}`} className="mp-odds-card">
                    <div className="mp-odds-card-header">{(market.mn || MARKET_NAMES[market.mtid] || `Market ${market.mtid}`) + sovLabel(market)}</div>
                    <div className="mp-odds-card-body">
                      {market.outcomes.map((oc) => (
                        <div key={oc.n}
                          className={`mp-odds-cell mp-odds-cell-click`}
                          onClick={() => handleOddClick(event, market, oc)}>
                          <span className="mp-odds-cell-lbl">{oc.name}</span>
                          <span className="mp-odds-cell-val">{parseFloat(oc.odd).toFixed(2)}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                ));
              })}
            </div>
          </div>
        ))}
      </div>
    );
  };

  /* ── Bet handlers ── */
  const handleOddClick = (event, market, outcome) => {
    setSelectedBet({ event, market, outcome });
    setBetResult(null);
  };

  const handleConfirmBet = async () => {
    if (!selectedBet || betLoading || selectedAccounts.length === 0) return;
    setBetLoading(true);
    try {
      const res = await fetch(`${API_BASE}/goals/manual-bet`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({
          eventCode: selectedBet.event.code,
          marketId: selectedBet.market.id,
          outcomeN: selectedBet.outcome.n,
          usernames: selectedAccounts,
        }),
      });
      const data = await res.json();
      setBetResult(data.success ? data.data : { success: false, error: data.error });
    } catch (err) {
      setBetResult({ success: false, error: err.message });
    }
    setBetLoading(false);
  };

  const closeModal = () => { setSelectedBet(null); setBetResult(null); };

  /* ── Render ── */
  const liveEvents = events.filter(e => e.status !== 5);
  const displayEvents = only2X5 ? liveEvents.filter(e => /2\s*[xX×]\s*5/i.test(e.league || '')) : liveEvents;

  return (
    <div className="espor-results">
      {/* Header */}
      <div className="er-hdr">
        <div className="er-hdr-l">
          <h2>🎮 Espor Monitör</h2>
          <span className="er-cnt">{displayEvents.length} maç{only2X5 ? '' : ` / ${liveEvents.length}`}</span>
          {lastRefresh && <span className="mp-refresh-ts">Son: {lastRefresh.toLocaleTimeString('tr-TR', { timeZone: 'Europe/Istanbul' })}</span>}
        </div>
        <div className="mp-hdr-center">
          <span className={`gs-pill mp-hdr-pill ${globalMode === 'cizgi' ? 'gs-pill-on' : 'gs-pill-off'}`} onClick={() => setGlobalModeAll('cizgi')}>📊 Çizgi</span>
          <span className={`gs-pill mp-hdr-pill ${globalMode === 'liste' ? 'gs-pill-on' : 'gs-pill-off'}`} onClick={() => setGlobalModeAll('liste')}>📋 Liste</span>
          <span className={`gs-pill mp-hdr-pill ${globalMode === 'gizle' ? 'gs-pill-on' : 'gs-pill-off'}`} onClick={() => setGlobalModeAll('gizle')}>✕ Gizle</span>
          <span className="mp-hdr-sep" />
          <span className={`gs-pill mp-hdr-pill ${globalOddsMode === 'hizli' ? 'gs-pill-on' : 'gs-pill-off'}`} onClick={() => setGlobalOdds(globalOddsMode === 'hizli' ? 'gizli' : 'hizli')}>⚡ Hızlı</span>
          <span className={`gs-pill mp-hdr-pill ${globalOddsMode === 'tumu' ? 'gs-pill-on' : 'gs-pill-off'}`} onClick={() => setGlobalOdds(globalOddsMode === 'tumu' ? 'gizli' : 'tumu')}>📈 Oranlar</span>
        </div>
        <div className="er-hdr-r">
          <button className={`er-refresh ${only2X5 ? 'er-gmbtn-on' : ''}`} onClick={() => setOnly2X5(p => !p)} title="Sadece 2X5 maçları göster">
            🏆 {only2X5 ? 'Sadece 2X5' : 'Tümü'}
          </button>
          <button className="er-refresh" onClick={() => apply2x5(liveEvents)} title="2x5 maçları çizgi+hızlı">⚽ 2X5</button>
          <button className="er-refresh" onClick={() => apply2x5Full(liveEvents)} title="2x5 maçları çizgi+tüm oranlar">⚽ 2X5+</button>
          <button className="er-refresh" onClick={() => { setGlobalModeAll('cizgi'); setGlobalOdds('hizli'); }} title="Tümü çizgi+hızlı">⚡ Tümü</button>
          <button className="er-refresh" onClick={() => { setGlobalModeAll('cizgi'); setGlobalOdds('tumu'); }} title="Tümü çizgi+tüm oranlar">📈 Tümü+</button>
          <button className="er-refresh" onClick={() => { setGlobalModeAll('gizle'); setGlobalOdds('gizli'); }} title="Tümünü kapat">🔒 Kapat</button>
          <button className="er-refresh" onClick={() => fetchEvents(true)} disabled={loading}>{loading ? '⏳' : '🔄'} Yenile</button>
        </div>
      </div>

      {/* Account Selection Bar */}
      <div className="mp-settings-bar">
        <div className="mp-sbar-group">
          <label className="mp-sbar-label">Hesaplar</label>
          <div className="mp-account-checks">
            {loggedInAccounts.length === 0 ? (
              <span style={{ color: '#888', fontSize: 12 }}>Giriş yapılmış hesap yok</span>
            ) : loggedInAccounts.map(acc => (
              <label key={acc.username} className="mp-account-check">
                <input type="checkbox" checked={selectedAccounts.includes(acc.username)}
                  onChange={e => {
                    setSelectedAccounts(prev => e.target.checked
                      ? [...prev, acc.username]
                      : prev.filter(u => u !== acc.username));
                  }} />
                <span className="mp-account-name">{acc.displayName || acc.username}</span>
                {acc.balance != null && <span className="mp-account-bal">{Number(acc.balance).toFixed(0)}₺</span>}
              </label>
            ))}
          </div>
        </div>
      </div>

      {/* Cards */}
      {displayEvents.length === 0 ? (
        <div className="er-empty"><span className="er-empty-icon">📭</span><p>{only2X5 ? 'Şu anda 2X5 e-futbol maçı yok.' : 'Şu anda izlenen e-futbol maçı yok.'}</p></div>
      ) : (
        <div className="er-cards">
          {displayEvents.map((event) => {
            const id = event.code;
            const hp = playerOf(event.homeTeam);
            const ap = playerOf(event.awayTeam);
            const hc = clubOf(event.homeTeam);
            const ac = clubOf(event.awayTeam);
            const hw = event.homeScore > event.awayScore;
            const aw = event.awayScore > event.homeScore;
            const t = fmtTime(event.startTime);
            const mode = modeOf(id);
            const matchMin = getMatchMinute(event.league, event.startTime, event.status);
            const iy = event.halfTimeScore ? `${event.halfTimeScore.home}-${event.halfTimeScore.away}` : null;

            return (
              <div key={id} className="er-card">
                <div className="er-card-league">{event.league || '-'}</div>
                <div className="er-card-body">
                  <div className="er-card-time">
                    <div className="er-time1">{t.line1}</div>
                    <div className="er-time2">{t.line2}</div>
                  </div>
                  <div className={`er-card-player er-card-hp ${hw ? 'er-w' : aw ? 'er-l' : 'er-d'}`}
                    onClick={() => onNavigate && onNavigate({ type: 'player', name: hp })}>
                    <span className="er-pn">{hp || hc}</span>
                    <span className="er-cn">{hc}</span>
                  </div>
                  <div className="er-card-score"
                    onClick={() => onNavigate && onNavigate({ type: 'h2h', homePlayer: hp, awayPlayer: ap })}>
                    <span className={`er-sb ${hw ? 'er-sb-hw' : aw ? 'er-sb-aw' : 'er-sb-d'}`}>{event.homeScore}-{event.awayScore}</span>
                    {matchMin && (
                      <div className={`mp-minute ${matchMin.half > 0 ? 'mp-minute-live' : matchMin.label === 'MS' ? 'mp-minute-ms' : 'mp-minute-other'}`}>
                        {matchMin.label}
                      </div>
                    )}
                    {iy && <span className="er-iy">İY {iy}</span>}
                  </div>
                  <div className={`er-card-player er-card-ap ${aw ? 'er-w' : hw ? 'er-l' : 'er-d'}`}
                    onClick={() => onNavigate && onNavigate({ type: 'player', name: ap })}>
                    <span className="er-pn">{ap || ac}</span>
                    <span className="er-cn">{ac}</span>
                  </div>
                  <div className="er-card-actions">
                    {renderModeButtons(id)}
                  </div>
                </div>

                {/* Goal detail */}
                {mode !== 'gizle' && (
                  <div className="er-card-detail">
                    {mode === 'cizgi' ? renderGoalTimeline(event) : renderGoalList(event)}
                  </div>
                )}

                {/* Odds — toggled via ⚡/📈 buttons */}
                {oddsModeFor(id) !== 'gizli' && renderOddsPanel(event, oddsModeFor(id))}
              </div>
            );
          })}
        </div>
      )}

      {/* Bet Confirmation Modal */}
      {selectedBet && (
        <div className="mp-modal-overlay" onClick={closeModal}>
          <div className="mp-modal" onClick={(e) => e.stopPropagation()}>
            {!betResult ? (
              <>
                <div className="mp-modal-header">
                  <h3>🎫 Kupon Onayla</h3>
                  <button className="mp-modal-close" onClick={closeModal}>✕</button>
                </div>
                <div className="mp-modal-body">
                  <div className="mp-modal-match">
                    <span className="mp-modal-teams">{selectedBet.event.homeTeam} vs {selectedBet.event.awayTeam}</span>
                    <span className="mp-modal-score">{selectedBet.event.homeScore} - {selectedBet.event.awayScore}</span>
                  </div>
                  <div className="mp-modal-details">
                    <div className="mp-modal-row">
                      <span className="mp-modal-label">Market</span>
                      <span className="mp-modal-val">{selectedBet.market.mn || MARKET_NAMES[selectedBet.market.mtid] || `Market ${selectedBet.market.mtid}`}</span>
                    </div>
                    <div className="mp-modal-row">
                      <span className="mp-modal-label">Seçim</span>
                      <span className="mp-modal-val mp-modal-highlight">{selectedBet.outcome.name}</span>
                    </div>
                    <div className="mp-modal-row">
                      <span className="mp-modal-label">Oran</span>
                      <span className="mp-modal-val mp-modal-highlight">{parseFloat(selectedBet.outcome.odd).toFixed(2)}</span>
                    </div>
                  </div>

                  <div className="mp-modal-section">
                    <div className="mp-modal-section-title">� Hesaplar ({selectedAccounts.length})</div>
                    {selectedAccounts.map(u => {
                      const acc = loggedInAccounts.find(a => a.username === u);
                      return (
                        <div key={u} className="mp-modal-row">
                          <span className="mp-modal-label">{acc?.displayName || u}</span>
                          <span className="mp-modal-val">{acc?.balance != null ? `${Number(acc.balance).toFixed(2)} ₺` : '—'}</span>
                        </div>
                      );
                    })}
                  </div>
                </div>
                <div className="mp-modal-footer">
                  <button className="mp-btn-cancel" onClick={closeModal}>İptal</button>
                  <button className="mp-btn-confirm" onClick={() => handleConfirmBet()} disabled={betLoading}>
                    {betLoading ? '⏳ Oynanıyor...' : '✅ Oyna'}
                  </button>
                </div>
              </>
            ) : (
              <>
                <div className="mp-modal-header">
                  <h3>{Array.isArray(betResult) ? (betResult.some(r => r.success) ? '✅ Kupon Oynandı' : '❌ Kupon Başarısız') : (betResult.success ? '✅ Kupon Oynandı' : '❌ Kupon Başarısız')}</h3>
                  <button className="mp-modal-close" onClick={closeModal}>✕</button>
                </div>
                <div className="mp-modal-body">
                  {Array.isArray(betResult) ? betResult.map((r, i) => (
                    <div key={i} className={`mp-result-panel ${r.success ? 'mp-result-success' : 'mp-result-fail'}`} style={{ marginBottom: 8 }}>
                      <div className="mp-result-row"><span className="mp-result-label">Hesap</span><span className="mp-result-val">{r.nesineUser || '?'}</span></div>
                      {r.barcode && <div className="mp-result-row"><span className="mp-result-label">Barkod</span><span className="mp-result-barcode">{r.barcode}</span></div>}
                      {r.odd && <div className="mp-result-row"><span className="mp-result-label">Oran</span><span className="mp-result-val">{r.odd}</span></div>}
                      {r.potentialWin && <div className="mp-result-row"><span className="mp-result-label">Kazanç</span><span className="mp-result-val">{r.potentialWin} TL</span></div>}
                      {r.playErrorCode && <div className="mp-result-row"><span className="mp-result-label">Play Hata</span><span className={`mp-result-errcode ${r.playErrorCode === '51-253' ? 'mp-errcode-warn' : 'mp-errcode-fail'}`}>{r.playErrorCode}{r.success ? ' (Validate ile çözüldü)' : ''}</span></div>}
                      {r.error && <div className="mp-result-row"><span className="mp-result-label">Hata</span><span className="mp-result-error">{r.error}</span></div>}
                    </div>
                  )) : (
                    <div className={`mp-result-panel ${betResult.success ? 'mp-result-success' : 'mp-result-fail'}`}>
                      {betResult.error && <div className="mp-result-row"><span className="mp-result-label">Hata</span><span className="mp-result-error">{betResult.error}</span></div>}
                    </div>
                  )}
                </div>
                <div className="mp-modal-footer">
                  <button className="mp-btn-cancel" onClick={closeModal}>Kapat</button>
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export default ManualPlay;
